import { ActionIcon, Button, Group, NumberInput, Select, Table, TextInput, Text } from '@mantine/core'
import { IconPlus, IconTrash } from '@tabler/icons-react'
import type { RequerimientoItemEntrada } from '@serkat/shared'
import { aSelect, useCatalogos } from '../../../api/catalogos'

interface Props {
  items: RequerimientoItemEntrada[]
  errores: Record<string, string | undefined>
  onCambiar: (items: RequerimientoItemEntrada[]) => void
}

/**
 * Las líneas de detalle del LOG-FT-01. La plantilla tiene 23 filas fijas; acá
 * se agregan las que hagan falta y el número de ítem se renumera solo.
 */
export function TablaItems({ items, errores, onCambiar }: Props) {
  const { data: catalogos } = useCatalogos()
  const unidades = aSelect(catalogos?.unidades)

  function renumerar(lista: RequerimientoItemEntrada[]) {
    return lista.map((it, i) => ({ ...it, itemNumero: i + 1 }))
  }

  function agregar() {
    onCambiar(
      renumerar([
        ...items,
        {
          itemNumero: items.length + 1,
          cantidad: 1,
          unidadId: catalogos?.unidades[0]?.id ?? 0,
          insumoServicio: '',
          codPartida: null,
          insumoId: null,
          descripcionMarca: null,
          lugarDeUso: null,
        },
      ]),
    )
  }

  function quitar(indice: number) {
    onCambiar(renumerar(items.filter((_, i) => i !== indice)))
  }

  function editar(indice: number, campo: keyof RequerimientoItemEntrada, valor: unknown) {
    onCambiar(items.map((it, i) => (i === indice ? { ...it, [campo]: valor } : it)))
  }

  return (
    <div>
      <Group justify="space-between" mb="xs">
        <Text fw={550} size="sm">Ítems del requerimiento</Text>
        <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={agregar}>
          Agregar ítem
        </Button>
      </Group>

      {errores['items'] && (
        <Text c="red" size="xs" mb="xs">{errores['items']}</Text>
      )}

      <Table.ScrollContainer minWidth={760}>
        <Table withTableBorder verticalSpacing={4}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={44}>#</Table.Th>
              <Table.Th w={110}>Cantidad</Table.Th>
              <Table.Th w={130}>Unidad</Table.Th>
              <Table.Th w={120}>Cód. partida</Table.Th>
              <Table.Th>Insumo / servicio</Table.Th>
              <Table.Th w={170}>Descripción / marca</Table.Th>
              <Table.Th w={150}>Lugar de uso</Table.Th>
              <Table.Th w={44} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={8}>
                  <Text c="dimmed" size="sm" ta="center" py="sm">
                    Sin ítems. Agregue al menos uno.
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
            {items.map((it, i) => (
              <Table.Tr key={i}>
                <Table.Td ta="center" c="dimmed">{it.itemNumero}</Table.Td>
                <Table.Td>
                  <NumberInput
                    size="xs" min={0.0001} decimalScale={4} hideControls
                    value={it.cantidad}
                    onChange={(v) => editar(i, 'cantidad', Number(v) || 0)}
                    error={Boolean(errores[`items.${i}.cantidad`])}
                  />
                </Table.Td>
                <Table.Td>
                  <Select
                    size="xs" data={unidades} searchable
                    value={it.unidadId ? String(it.unidadId) : null}
                    onChange={(v) => editar(i, 'unidadId', Number(v))}
                    error={Boolean(errores[`items.${i}.unidadId`])}
                  />
                </Table.Td>
                <Table.Td>
                  <TextInput size="xs" value={it.codPartida ?? ''}
                    onChange={(e) => editar(i, 'codPartida', e.currentTarget.value)} />
                </Table.Td>
                <Table.Td>
                  <TextInput
                    size="xs" value={it.insumoServicio}
                    onChange={(e) => editar(i, 'insumoServicio', e.currentTarget.value)}
                    error={Boolean(errores[`items.${i}.insumoServicio`])}
                  />
                </Table.Td>
                <Table.Td>
                  <TextInput size="xs" value={it.descripcionMarca ?? ''}
                    onChange={(e) => editar(i, 'descripcionMarca', e.currentTarget.value)} />
                </Table.Td>
                <Table.Td>
                  <TextInput size="xs" value={it.lugarDeUso ?? ''}
                    onChange={(e) => editar(i, 'lugarDeUso', e.currentTarget.value)} />
                </Table.Td>
                <Table.Td>
                  <ActionIcon variant="subtle" color="red" onClick={() => quitar(i)}>
                    <IconTrash size={15} />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </div>
  )
}
